import { useQuery } from '@tanstack/react-query'
import { ExternalLink, Clock, CheckCircle, XCircle, Loader } from 'lucide-react'
import api from '../lib/api'

interface Deployment {
  id: string
  projectId: string
  environment: 'production' | 'staging' | 'development'
  branch: string
  commitHash?: string
  status: 'pending' | 'building' | 'deploying' | 'completed' | 'failed' | 'cancelled'
  url?: string
  createdAt: string
}

interface DeploymentListResponse {
  data: Deployment[]
}

const statusIcons: Record<string, any> = {
  pending: Clock,
  building: Loader,
  deploying: Loader,
  completed: CheckCircle,
  failed: XCircle,
  cancelled: XCircle
}

const statusColors: Record<string, string> = {
  pending: 'text-gray-400',
  building: 'text-yellow-400',
  deploying: 'text-blue-400',
  completed: 'text-green-400',
  failed: 'text-red-400',
  cancelled: 'text-gray-400'
}

export default function Deployments() {
  const { data, isLoading } = useQuery({
    queryKey: ['deployments'],
    queryFn: () => api.get<DeploymentListResponse>('/deploy/list').then(res => res.data)
  })

  if (isLoading) return <div>Loading...</div>

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Deployments</h2>

      <div className="grid gap-4">
        {data?.data?.map((deploy: Deployment) => {
          const StatusIcon = statusIcons[deploy.status] || Clock
          return (
            <div key={deploy.id} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold">{deploy.projectId}</h3>
                    <span className={`px-2 py-1 rounded text-xs ${
                      deploy.environment === 'production' ? 'bg-red-900 text-red-300' :
                      deploy.environment === 'staging' ? 'bg-yellow-900 text-yellow-300' :
                      'bg-blue-900 text-blue-300'
                    }`}>
                      {deploy.environment}
                    </span>
                  </div>
                  <p className="text-sm text-gray-400">
                    Branch: {deploy.branch} {deploy.commitHash && `• Commit: ${deploy.commitHash.slice(0, 7)}`}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {new Date(deploy.createdAt).toLocaleString()}
                  </p>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <StatusIcon className={`w-5 h-5 ${statusColors[deploy.status]}`} />
                    <span className="text-sm font-medium">{deploy.status}</span>
                  </div>
                  {deploy.url && (
                    <a
                      href={deploy.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
